import { ForwardRefExoticComponent, forwardRef } from 'react'
import {
  default as MuiTextField,
  TextFieldProps as MuiTextFieldProps
} from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import ReportSharpIcon from '@mui/icons-material/ReportSharp'
import clsx from 'clsx'

export type TextFieldProps = Pick<
  MuiTextFieldProps,
  | 'autoComplete'
  | 'autoFocus'
  | 'children'
  | 'defaultValue'
  | 'disabled'
  | 'error'
  | 'fullWidth'
  | 'helperText'
  | 'hiddenLabel'
  | 'id'
  | 'inputRef'
  | 'InputLabelProps'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'onFocus'
  | 'placeholder'
  | 'ref'
  | 'required'
  | 'type'
  | 'value'
  | 'rows'
  | 'multiline'
  | 'minRows'
  | 'maxRows'
  | 'InputProps'
  | 'inputProps'
  | 'SelectProps'
  | 'onKeyUp'
  | 'onKeyDownCapture'
> & {
  label: string
  /**
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  startAdornment?: string | JSX.Element
  endAdornment?: string | JSX.Element
  focused?: boolean
}

/**
 * TextField is a styled variant of MUI's TextField.
 *
 * More info can be found at: https://mui.com/material-ui/react-text-field/
 */
export const TextField: ForwardRefExoticComponent<TextFieldProps> = forwardRef(
  (
    {
      startAdornment,
      endAdornment,
      fullWidth,
      label,
      hiddenLabel,
      error,
      size = 'medium',
      InputProps,
      focused,
      inputProps,
      ...rest
    },
    ref
  ) => {
    if (!label?.length && !hiddenLabel)
      throw new Error('A non-empty string must be provided as a label to ensure accessibility.')

    const shouldHideLabel = hiddenLabel || size === 'small'

    return (
      <MuiTextField
        ref={ref}
        label={shouldHideLabel ? undefined : label}
        fullWidth={fullWidth}
        error={error}
        hiddenLabel={shouldHideLabel}
        inputProps={{
          ...inputProps,
          ...(shouldHideLabel && { 'aria-label': label })
        }}
        {...rest}
        InputProps={{
          disableUnderline: true,
          startAdornment: startAdornment && (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ),
          endAdornment: error ? (
            <InputAdornment position="end">
              <ReportSharpIcon sx={{ color: theme => theme.palette.error.main }} />
              {endAdornment}
            </InputAdornment>
          ) : (
            endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>
          ),
          ...InputProps
        }}
        FormHelperTextProps={{
          role: error ? 'alert' : undefined
        }}
        className={clsx({
          'Mui-disabled': rest.disabled,
          'MuiTextField-sizeSmall': size === 'small',
          'MuiTextField-sizeMedium': size === 'medium',
          'MuiTextField-sizeLarge': size === 'large',
          focused: focused
        })}
      />
    )
  }
)

TextField.displayName = 'TextField'

declare module '@mui/material/TextField' {
  interface TextFieldPropsVariantOverrides {
    standard: false
    outlined: false
  }

  interface TextFieldPropsSizeOverrides {
    large: true
  }

  interface TextFieldClasses {
    sizeLarge: string
  }
}
